import { AlertTriangle, Check, Circle, Loader2, X } from 'lucide-react';
import type { RefreshProgress as Progress, RefreshStep, RefreshStepState } from '../lib/api';
import { cn } from '../lib/utils';
import { LoadingTicker } from './LoadingTicker';

const STEP_LABEL: Record<RefreshStep, string> = {
  snapshot: 'Previous snapshot',
  rms: 'Teltonika RMS gateways',
  sims: 'Hologram SIMs',
  billing: 'Hologram billing usage',
  daily: 'Hologram daily usage',
  rates: 'Plan rates',
  ledger: 'Hologram charges',
  mobileSince: 'RMS 4G history',
  store: 'Saving',
};

const ORDER: RefreshStep[] = ['snapshot', 'rms', 'sims', 'billing', 'daily', 'rates', 'ledger', 'mobileSince', 'store'];
const HOLOGRAM: RefreshStep[] = ['sims', 'billing', 'daily', 'rates', 'ledger'];

/**
 * The live checklist of a background refresh: each step as the server reports
 * it, with how long the finished ones took. Shown only while a run is going —
 * once it ends the page reloads, and anything that kept an old copy is named
 * in the page's own notices.
 */
export function RefreshProgress({ progress }: { progress: Progress | null }) {
  if (!progress || progress.state !== 'running') return null;
  const done = ORDER.filter((s) => progress.steps[s].state !== 'pending' && progress.steps[s].state !== 'running').length;
  const waiting = ORDER.filter((s) => progress.steps[s].state === 'running');
  const waitingText = waiting.length
    ? `Waiting on ${waiting.map((s) => STEP_LABEL[s]).join(', ')}…`
    : 'Starting…';
  const onHologram = waiting.some((s) => HOLOGRAM.includes(s));

  return (
    <div className="rounded-md border border-line bg-panel px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="font-medium">
          Refreshing from RMS and Hologram · {done} of {ORDER.length}
        </span>
        <LoadingTicker
          active
          steps={[waitingText]}
          slowAfterMs={12_000}
          slowMessage={
            onHologram
              ? `${waitingText} Hologram is slow right now; anything it cannot answer keeps its previous copy.`
              : undefined
          }
        />
      </div>
      <ol className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {ORDER.map((step) => {
          const s = progress.steps[step];
          return (
            <li key={step} className={cn('inline-flex items-center gap-1', stateClass(s.state))} title={s.note}>
              <StepIcon state={s.state} />
              {STEP_LABEL[step]}
              {s.endedAt !== undefined && s.startedAt !== undefined && (
                <span className="tabular-nums text-muted-foreground">{seconds(s.endedAt - s.startedAt)}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StepIcon({ state }: { state: RefreshStepState }) {
  switch (state) {
    case 'running':
      return <Loader2 className="h-3 w-3 animate-spin" />;
    case 'done':
      return <Check className="h-3 w-3 text-ok" />;
    case 'stale':
      return <AlertTriangle className="h-3 w-3 text-caution" />;
    case 'failed':
      return <X className="h-3 w-3 text-destructive" />;
    default:
      return <Circle className="h-3 w-3 text-muted-foreground/50" />;
  }
}

function stateClass(state: RefreshStepState): string {
  if (state === 'pending') return 'text-muted-foreground';
  if (state === 'stale') return 'text-caution';
  if (state === 'failed') return 'text-destructive';
  return '';
}

function seconds(ms: number): string {
  return ms < 1_000 ? `${Math.max(ms, 0)}ms` : `${(ms / 1_000).toFixed(1)}s`;
}
